/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
// Utility to fetch settings (minimal, for logo)
function usePanelSettings() {
  const [settings, setSettings] = useState({});
  useEffect(() => {
    // The public name -> value map; the plain /api/settings list needs a permission.
    adminApi
      .getSettingsMap()
      .then((data) => setSettings(data || {}))
      .catch(() => {});
  }, []);
  return settings;
}

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import MenuList from "@mui/material/MenuList";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationMenu from "./NotificationMenu";
import CallBar from "./CallBar";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setDarkMode,
} from "context";

// API
import { adminApi } from "services/api";

import { saveDarkMode } from "utils/themePreference";
import { DARK_LOGO_FILTER } from "utils/lightDark";

function DashboardNavbar({ absolute, light, isMini }) {
  const settings = usePanelSettings();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchMenuAnchor, setSearchMenuAnchor] = useState(null);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  // Below md the search box collapses to an icon that opens a full-width row under the bar.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener("scroll", handleTransparentNavbar);
      // Clear search debounce timer on cleanup
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [dispatch, fixedNavbar, searchDebounceTimer]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleDarkModeToggle = () => {
    setDarkMode(dispatch, !darkMode);
    saveDarkMode(!darkMode);
  };

  // Search functionality with debouncing
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    // Clear previous timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    if (query.trim().length < 2) {
      setSearchResults([]);
      setSearchMenuAnchor(null);
      setSearchLoading(false);
      return;
    }

    setSearchMenuAnchor(event.currentTarget);
    setSearchLoading(true);

    // Debounce search - wait 500ms after user stops typing
    const timer = setTimeout(async () => {
      try {
        // Search across multiple content types
        const [blogPostsRes, servicesRes, bootcampsRes, pagesRes, teamMembersRes] =
          await Promise.all([
            adminApi.getBlogPosts({ limit: 1000 }).catch(() => null),
            adminApi.getServices().catch(() => null),
            adminApi.getBootcamps().catch(() => null),
            adminApi.getPages().catch(() => null),
            adminApi.getTeamMembers().catch(() => null),
          ]);

        const results = [];
        const queryLower = query.toLowerCase();

        // Helper to ensure array
        const toArray = (data) => {
          if (!data) return [];
          if (Array.isArray(data)) return data;
          if (data.data && Array.isArray(data.data)) return data.data;
          if (data.posts && Array.isArray(data.posts)) return data.posts; // GET /blog: { posts, pagination }
          return [];
        };

        // Add blog posts
        const blogPosts = toArray(blogPostsRes);
        blogPosts
          .filter((post) => {
            if (!post) return false;
            const title = (post.title || "").toLowerCase();
            const content = (post.content || "").toLowerCase();
            return title.includes(queryLower) || content.includes(queryLower);
          })
          .slice(0, 3)
          .forEach((post) => {
            results.push({
              type: "Blog Post",
              title: post.title || "Untitled Post",
              icon: "article",
              route: `/blog-posts`,
              id: post._id,
            });
          });

        // Add services
        const services = toArray(servicesRes);
        services
          .filter((service) => {
            if (!service) return false;
            const title = (service.title || "").toLowerCase();
            const description = (service.shortDescription || "").toLowerCase();
            return title.includes(queryLower) || description.includes(queryLower);
          })
          .slice(0, 3)
          .forEach((service) => {
            results.push({
              type: "Service",
              title: service.title || "Untitled Service",
              icon: "business_center",
              route: `/services`,
              id: service._id,
            });
          });

        // Add bootcamps
        const bootcamps = toArray(bootcampsRes);
        bootcamps
          .filter((bootcamp) => {
            if (!bootcamp) return false;
            const title = (bootcamp.title || "").toLowerCase();
            const description = (bootcamp.description || "").toLowerCase();
            return title.includes(queryLower) || description.includes(queryLower);
          })
          .slice(0, 3)
          .forEach((bootcamp) => {
            results.push({
              type: "Bootcamp",
              title: bootcamp.title || "Untitled Bootcamp",
              icon: "school",
              route: `/bootcamps`,
              id: bootcamp._id,
            });
          });

        // Add pages
        const pages = toArray(pagesRes);
        pages
          .filter((page) => {
            if (!page) return false;
            const title = (page.title || "").toLowerCase();
            const content = (page.content || "").toLowerCase();
            return title.includes(queryLower) || content.includes(queryLower);
          })
          .slice(0, 3)
          .forEach((page) => {
            results.push({
              type: "Page",
              title: page.title || "Untitled Page",
              icon: "description",
              route: `/pages`,
              id: page._id,
            });
          });

        // Add team members
        const teamMembers = toArray(teamMembersRes);
        teamMembers
          .filter((member) => {
            if (!member) return false;
            const name = (member.name || "").toLowerCase();
            const position = (member.position || "").toLowerCase();
            return name.includes(queryLower) || position.includes(queryLower);
          })
          .slice(0, 3)
          .forEach((member) => {
            results.push({
              type: "Team Member",
              title: member.name || "Unnamed Member",
              icon: "people",
              route: `/team-members`,
              id: member._id,
            });
          });

        setSearchResults(results);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500); // 500ms debounce

    setSearchDebounceTimer(timer);
  };

  const handleSearchResultClick = (result) => {
    navigate(result.route);
    setSearchQuery("");
    setSearchResults([]);
    setSearchMenuAnchor(null);
    setSearchLoading(false);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") setSearchMenuAnchor(null);
  };

  const handleSearchBlur = () => {
    // Delay closing to allow click events to fire
    setTimeout(() => {
      setSearchMenuAnchor(null);
    }, 200);
  };

  // Render search results. A non-modal Popper (not a Menu): a Menu is a modal that takes keyboard
  // focus, which swallowed the next letters typed and blurred the box, closing the results.
  const renderSearchMenu = () => (
    <Popper
      anchorEl={searchMenuAnchor}
      open={Boolean(searchMenuAnchor)}
      placement="bottom-start"
      sx={{ zIndex: 1300 }}
    >
      <Paper
        elevation={8}
        sx={{
          mt: 2,
          width: { xs: "calc(100vw - 24px)", sm: 350 },
          maxWidth: 350,
          maxHeight: 400,
          overflow: "auto",
        }}
        // Keep focus in the search box when a result is pressed, so it isn't blurred before the click lands.
        onMouseDown={(event) => event.preventDefault()}
      >
        <MenuList disablePadding>
          {searchLoading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress size={30} />
            </MDBox>
          ) : searchResults.length > 0 ? (
            <>
              <MDBox px={2} pt={1} pb={0.5}>
                <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                  Search Results ({searchResults.length})
                </MDTypography>
              </MDBox>
              <Divider sx={{ my: 0.5 }} />
              {searchResults.map((result, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleSearchResultClick(result)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Icon sx={{ mr: 2, color: "primary.main" }}>{result.icon}</Icon>
                  <MDBox>
                    <MDTypography variant="button" fontWeight="medium" display="block">
                      {result.title}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      {result.type}
                    </MDTypography>
                  </MDBox>
                </MenuItem>
              ))}
            </>
          ) : searchQuery.trim().length >= 2 ? (
            <MDBox px={2} py={3} textAlign="center">
              <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}>search_off</Icon>
              <MDTypography variant="body2" color="text">
                No results found for &quot;{searchQuery}&quot;
              </MDTypography>
            </MDBox>
          ) : null}
        </MenuList>
      </Paper>
    </Popper>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          color="inherit"
          sx={(theme) => ({
            ...navbarRow(theme, { isMini }),
            flex: { xs: "1 1 0", md: "0 1 auto" },
          })}
        >
          <IconButton
            size="small"
            disableRipple
            color="inherit"
            sx={(theme) => ({ ...navbarMobileMenu(theme), mr: 0.5, ml: -0.5 })}
            onClick={handleMiniSidenav}
            aria-label={miniSidenav ? "Open menu" : "Close menu"}
          >
            <Icon sx={iconsStyle} fontSize="medium">
              {miniSidenav ? "menu" : "menu_open"}
            </Icon>
          </IconButton>
          <MDBox
            component={Link}
            to="/dashboard"
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            <img
              src={settings.site_logo || "/apple-icon.png"}
              alt="Elite 247 Logo"
              style={{
                height: 40,
                width: 40,
                objectFit: "contain",
                filter: darkMode && !settings.site_logo ? DARK_LOGO_FILTER : undefined,
              }}
            />
          </MDBox>
          <MDBox minWidth={0}>
            <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          </MDBox>
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => ({ ...navbarRow(theme, { isMini }), flexShrink: 0 })}>
            <MDBox pr={1} sx={{ display: { xs: "none", md: "block" } }}>
              <MDInput
                label={searchLoading ? "Searching..." : "Search here"}
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={(e) => {
                  if (searchQuery.trim().length >= 2 && searchResults.length > 0) {
                    setSearchMenuAnchor(e.currentTarget);
                  }
                }}
                onBlur={handleSearchBlur}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search content..."
              />
            </MDBox>
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center">
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={(theme) => ({
                  ...navbarIconButton(theme),
                  display: { xs: "inline-flex", md: "none" },
                })}
                onClick={() => {
                  if (mobileSearchOpen) setSearchMenuAnchor(null); // its input is about to unmount
                  setMobileSearchOpen((open) => !open);
                }}
                aria-label="Search content"
              >
                <Icon sx={iconsStyle}>{mobileSearchOpen ? "search_off" : "search"}</Icon>
              </IconButton>
              <Tooltip title={darkMode ? "Light mode" : "Dark mode"}>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleDarkModeToggle}
                  aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                  <Icon sx={iconsStyle}>{darkMode ? "light_mode" : "dark_mode"}</Icon>
                </IconButton>
              </Tooltip>
              <Link to="/profile">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link>
              <Link to="/settings">
                <IconButton size="small" disableRipple color="inherit" sx={navbarIconButton}>
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>
              </Link>
              <NotificationMenu />
              <CallBar />
              {renderSearchMenu()}
            </MDBox>
          </MDBox>
        )}
        {!isMini && mobileSearchOpen && (
          <MDBox width="100%" pt={1} pb={0.5} sx={{ display: { xs: "block", md: "none" } }}>
            <MDInput
              fullWidth
              autoFocus
              label={searchLoading ? "Searching..." : "Search here"}
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={(e) => {
                if (searchQuery.trim().length >= 2 && searchResults.length > 0) {
                  setSearchMenuAnchor(e.currentTarget);
                }
              }}
              onBlur={handleSearchBlur}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search content..."
            />
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
